// Pure helpers for the one remaining way a toast can arrive from outside the
// client: in the URL. Server actions, route handlers, and emailed links can't
// call toast(), so they redirect to `?status=…&status_description=…` or
// `?error=…&error_description=…` (see getStatusRedirect / getErrorRedirect in
// utils/helpers.ts). UrlToastBridge.tsx consumes these on the client.
//
// Only these four keys are ever read or removed. Everything else in the query
// string (`disable_button`, `login_hint`, `authorization_id`, `redirect_url`,
// `token`, `activate`, `user_code`, `cb_error`, …) is form or flow state that
// other components own; it must pass through untouched.

export const TOAST_PARAM_KEYS = ['status', 'status_description', 'error', 'error_description'] as const;

export type UrlToast = {
  title: string;
  description: string | null;
  variant: 'default' | 'destructive';
};

/** `error` wins over `status` when both are present. Empty values don't toast. */
export function parseToastParams(params: URLSearchParams): UrlToast | null {
  const error = params.get('error');
  const status = params.get('status');
  if (error) {
    return { title: error, description: params.get('error_description') || null, variant: 'destructive' };
  }
  if (status) {
    return { title: status, description: params.get('status_description') || null, variant: 'default' };
  }
  return null;
}

/**
 * The query string with the toast keys removed (no leading `?`), or `null`
 * when none were present so callers can skip the history rewrite.
 */
export function stripToastParams(params: URLSearchParams): string | null {
  const next = new URLSearchParams(params.toString());
  let removed = false;
  for (const key of TOAST_PARAM_KEYS) {
    if (next.has(key)) {
      next.delete(key);
      removed = true;
    }
  }
  return removed ? next.toString() : null;
}
